import { averageConfidence, clampConfidence } from './confidence';
import { haversineDistanceMeters } from './distance';
import type { Coordinate, DailySummary, TimelineVisit, TripCandidate } from './models';
import { isNightStay } from './time';

type AnchorCandidate = {
  center: Coordinate;
  confidence: number;
};

function gridLabel(coordinate: Coordinate, precision = 1): string {
  return `${coordinate.lat.toFixed(precision)}, ${coordinate.lng.toFixed(precision)}`;
}

function clusterKey(coordinate: Coordinate): string {
  return `${coordinate.lat.toFixed(2)}_${coordinate.lng.toFixed(2)}`;
}

function estimateAnchor(visits: TimelineVisit[]): AnchorCandidate | null {
  if (visits.length === 0) return null;

  const nightVisits = visits.filter((visit) => isNightStay(visit.startTime, visit.endTime));
  const candidates = nightVisits.length > 0 ? nightVisits : visits.slice(0, 3);
  const clusters = new Map<string, { visits: TimelineVisit[]; totalMinutes: number }>();

  for (const visit of candidates) {
    const key = clusterKey(visit.center);
    const cluster = clusters.get(key) ?? { visits: [], totalMinutes: 0 };
    cluster.visits.push(visit);
    cluster.totalMinutes += visit.durationMinutes;
    clusters.set(key, cluster);
  }

  const best = [...clusters.values()].sort((a, b) => b.totalMinutes - a.totalMinutes)[0];
  if (!best) return null;

  const totalWeight = best.visits.reduce((sum, visit) => sum + Math.max(1, visit.durationMinutes), 0);
  const center = best.visits.reduce(
    (acc, visit) => ({
      lat: acc.lat + (visit.center.lat * Math.max(1, visit.durationMinutes)) / totalWeight,
      lng: acc.lng + (visit.center.lng * Math.max(1, visit.durationMinutes)) / totalWeight,
    }),
    { lat: 0, lng: 0 },
  );

  const distinctDays = new Set(best.visits.map((visit) => visit.startTime.slice(0, 10))).size;
  return {
    center,
    confidence: clampConfidence((nightVisits.length > 0 ? 0.5 : 0.25) + Math.min(0.4, distinctDays / 30) + averageConfidence(best.visits.map((visit) => visit.confidence)) * 0.1),
  };
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

function mainAreas(days: DailySummary[]): TripCandidate['mainAreas'] {
  const buckets = new Map<string, { lat: number; lng: number; count: number; days: Set<string> }>();
  for (const day of days) {
    for (const visit of day.topVisits) {
      const key = clusterKey(visit.center);
      const bucket = buckets.get(key) ?? { lat: 0, lng: 0, count: 0, days: new Set<string>() };
      bucket.lat += visit.center.lat;
      bucket.lng += visit.center.lng;
      bucket.count += 1;
      bucket.days.add(day.date);
      buckets.set(key, bucket);
    }
  }

  return [...buckets.values()]
    .sort((a, b) => b.days.size - a.days.size || b.count - a.count)
    .slice(0, 4)
    .map((bucket) => {
      const lat = bucket.lat / Math.max(1, bucket.count);
      const lng = bucket.lng / Math.max(1, bucket.count);
      return { label: `${gridLabel({ lat, lng })} 周辺`, lat, lng, days: bucket.days.size };
    });
}

export function detectTrips(dailySummaries: DailySummary[], visits: TimelineVisit[]): TripCandidate[] {
  const anchor = estimateAnchor(visits);
  if (!anchor) return [];

  const dailyDistances = dailySummaries.map((day) => day.distanceMeters);
  const usualDistance = Math.max(1000, median(dailyDistances));
  const farDays = dailySummaries.filter((day) => {
    const maxHomeDistance = Math.max(
      0,
      ...day.topVisits.map((visit) => haversineDistanceMeters(anchor.center, visit.center)),
    );
    const reasonCount =
      (maxHomeDistance >= 50_000 ? 1 : 0) +
      (day.distanceMeters >= usualDistance * 3 ? 1 : 0) +
      (day.stayMinutes >= 180 && maxHomeDistance >= 30_000 ? 1 : 0) +
      (day.tags.includes('travel') ? 1 : 0);
    return reasonCount >= 2;
  });

  const candidates: DailySummary[][] = [];
  let current: DailySummary[] = [];
  for (const day of dailySummaries) {
    if (farDays.includes(day)) {
      current.push(day);
    } else if (current.length > 0) {
      candidates.push(current);
      current = [];
    }
  }
  if (current.length > 0) candidates.push(current);

  return candidates
    .map((days, index): TripCandidate => {
      const homeDistanceKmMax = Math.max(
        0,
        ...days.flatMap((day) => day.topVisits.map((visit) => haversineDistanceMeters(anchor.center, visit.center) / 1000)),
      );
      const totalDistanceKm = days.reduce((sum, day) => sum + day.distanceMeters / 1000, 0);
      const areas = mainAreas(days);
      const reasons = [
        homeDistanceKmMax >= 50 ? `基準地点から最大 ${homeDistanceKmMax.toFixed(0)} km 離れています` : null,
        days.length >= 2 ? `${days.length} 日連続で通常生活圏外の可能性があります` : null,
        totalDistanceKm >= (usualDistance / 1000) * 3 ? '通常日の中央値より移動距離が大きいです' : null,
        areas.length > 0 ? '同一遠隔エリアで複数の滞在候補があります' : null,
      ].filter((reason): reason is string => Boolean(reason));

      return {
        id: `trip_${index}_${days[0].date}`,
        startDate: days[0].date,
        endDate: days[days.length - 1].date,
        title: areas[0] ? `${areas[0].label} への旅行候補` : '旅行/遠征候補',
        reason: reasons,
        homeDistanceKmMax,
        totalDistanceKm,
        mainAreas: areas,
        days,
        confidence: clampConfidence(0.25 + Math.min(0.25, days.length / 4) + Math.min(0.25, homeDistanceKmMax / 400) + anchor.confidence * 0.25),
      };
    })
    .filter((trip) => trip.days.length >= 2 || trip.confidence >= 0.55)
    .sort((a, b) => b.confidence - a.confidence);
}
